import { ChangeEvent, Dispatch, SetStateAction, useEffect } from 'react'
import { fetchInterestDeviation } from '../api/httpHandlers'
import { DEFAULT_PROPERTY_VALUE } from '../appConfig'
import { AppAction, AppState } from '../appState'
import useDebounce from '../hooks/useDebounce'
import {
  getEssentialDerivedState,
  getMaxDeposit,
  getMinDeposit,
  getPercentageValues
} from '../lib/calculationUtils'
import { formatValueForDisplay } from '../lib/formattingUtils'
import CustomInput, { IMessage } from './CustomInput'
import CustomSelect from './CustomSelect'
import { SliderEvent } from './CustomSlider'
import { TypographyH2 } from './typographyComponents'
import { NumberFormatValues } from 'react-number-format'

export type EventUnionType = SliderEvent | ChangeEvent<HTMLInputElement>

export function getField(event: EventUnionType) {
  if (Object.hasOwn(event, 'id')) return (event as SliderEvent).id
  return (event as ChangeEvent<HTMLInputElement>).currentTarget.id
}

export function getValue(event: EventUnionType) {
  const trimmedValue = event.target.value.replace(/\s+/g, '')
  return +trimmedValue
}

interface Props {
  appState: AppState
  appDispatch: Dispatch<AppAction>
  mortgageValues: {
    propertyValue: number
    downPayment: number
  }
  setMortgageValues: Dispatch<
    SetStateAction<{
      propertyValue: number
      downPayment: number
    }>
  >
}

function MortgageCalculator({ appState, appDispatch, mortgageValues, setMortgageValues }: Readonly<Props>) {
  const { houseObject, propertyValue, downPayment, personalLoanIsAdded, personalLoanAmount } =
    appState

  // State for use with debounce
  // 500 ms default delay
  const debouncedMortgageValues = useDebounce(mortgageValues)

  useEffect(() => {
    async function getInterestDeviation() {
      const response = await fetchInterestDeviation(propertyValue, propertyValue - downPayment)
      if (response.successful && response.data) {
        const interestDeviation = +response.data?.priceDifference
        appDispatch({ type: 'interestDeviationFetched', interestDeviation })
      } else {
        appDispatch({ type: 'error' })
      }
    }
    const { propertyValue, downPayment } = debouncedMortgageValues

    if (propertyValue - downPayment === 0 || propertyValue < appState.mortgageTerms.minHousePrice) {
      return appDispatch({
        type: 'interestDeviationFetched',
        interestDeviation: 0
      })
    }
    getInterestDeviation()
  }, [debouncedMortgageValues])

  const displayPrice = houseObject?.pris ?? DEFAULT_PROPERTY_VALUE
  const displaySource = houseObject?.utm_source ?? ''

  // We don't want users to enter a deposit larger than the price
  function downPaymentValueIsAllowed(value: NumberFormatValues) {
    const { floatValue } = value
    if (!floatValue) return false
    if (floatValue > appState.propertyValue || floatValue > mortgageValues.propertyValue)
      return false
    return true
  }

  // react-slider uses a custom onChange handler thats why we need this
  function onChangeHandler(event: EventUnionType) {
    const field = getField(event)
    if (field !== 'propertyValue' && field !== 'downPayment') return appDispatch({ type: 'error' })
    const numericValue = getValue(event)
    if (isNaN(numericValue)) return
    if (numericValue < 0) return
    appDispatch({ type: 'typing', value: true })
    if (field === 'propertyValue') {
      setMortgageValues({
        propertyValue: numericValue,
        downPayment: +(numericValue * 0.15).toFixed(0)
      })
    } else {
      setMortgageValues({ ...mortgageValues, downPayment: numericValue })
    }
    appDispatch({ type: 'fieldChange', field, value: numericValue })
  }

  function mortgageRateIndexOnChangeHandler(event: ChangeEvent<HTMLSelectElement>) {
    const field = event.currentTarget.id
    if (field !== 'mortgageRateIndex') return appDispatch({ type: 'error' })
    appDispatch({ type: 'fieldChange', field, value: +event.target.value })
  }

  const sliderMinMark = [propertyValue * 0.15]

  function getDownPaymentMessage() {
    const { effectivePersonalLoanAmount, showPersonalLoan, totalIsEnoughForPurchase } =
      getEssentialDerivedState(downPayment, propertyValue, personalLoanIsAdded, personalLoanAmount)
    const { downPaymentPercentage, personalLoanPercentage } = getPercentageValues(
      appState.propertyValue,
      appState.downPayment,
      effectivePersonalLoanAmount
    )
    if (downPaymentPercentage >= 15 && !showPersonalLoan) {
      const message: IMessage = {
        tone: 'positive',
        text: `Kontantinsatsen är ${formatValueForDisplay(
          downPaymentPercentage
        )}\u00A0% av slutpriset.`
      }
      return message
    }
    if (!totalIsEnoughForPurchase) {
      const message: IMessage = {
        tone: 'critical',
        text: 'Kontantinsatsen behöver vara minst 15\u00A0% av slutpriset. Du kan läsa om och lägga till ett privatlån nedan.'
      }
      return message
    }
    const totalPercentage = formatValueForDisplay(downPaymentPercentage + personalLoanPercentage)
    const personalLoanMessage =
      personalLoanPercentage > 0
        ? ` (varav ${formatValueForDisplay(personalLoanPercentage)}\u00A0% är privatlån)`
        : ''
    const message: IMessage = {
      tone: 'positive',
      text: `Kontantinsatsen är ${totalPercentage}\u00A0% av slutpriset${personalLoanMessage}.`
    }
    return message
  }

  const downPaymentMessage = getDownPaymentMessage()
  const downPaymentIsValid = downPaymentMessage.tone === 'positive'

  // If the query string does not include information we use a default value
  const propertyValueInformationMessage =
    appState.houseObject !== null &&
      (appState.houseObject?.pris === undefined || appState.houseObject?.pris === 0)
      ? 'Vi kunde inte hämta priset för den här bostaden och har därför lagt till en schablonkostnad.'
      : undefined

  return (
    <section className="border-2 border-neutral-80 p-[14px]">
      <TypographyH2>Bolån</TypographyH2>
      <div className="flex flex-wrap">
        <CustomInput
          id="propertyValue"
          label={
            appState?.houseObject?.nyproduktion || displaySource === 'egnahemsbolaget'
              ? 'Bostadens pris'
              : 'Vad tror du slutpriset blir?'
          }
          value={mortgageValues.propertyValue}
          inputChangeHandler={onChangeHandler}
          suffix="kr"
          maxLength={11}
          useSlider
          min={appState.mortgageTerms.minHousePrice}
          max={displayPrice * 1.5}
          sliderStep={10_000}
          additionalInformationMessage={propertyValueInformationMessage}
          isValidHandler={(value: number) => value >= appState.mortgageTerms.minHousePrice}
          errorMessage={appState.errorMessage}
        />
        <CustomInput
          id="downPayment"
          label="Kontantinsats"
          value={mortgageValues.downPayment}
          inputChangeHandler={onChangeHandler}
          suffix="kr"
          statusMessage={downPaymentMessage}
          maxLength={11}
          useSlider
          isValidHandler={() => downPaymentIsValid}
          errorMessage={downPaymentMessage.text}
          min={getMinDeposit(
            appState.propertyValue,
            appState.personalLoanTerms.maxPersonalLoanAmount,
            appState.personalLoanTerms.minPersonalLoanAmount,
            appState.mortgageTerms.maxLoanAmountMortgage,
            appState.mortgageTerms.minHousePrice
          )}
          max={getMaxDeposit(appState.propertyValue, appState.mortgageTerms.minLoanAmountMortgage)}
          sliderMinMark={sliderMinMark}
          valueIsAllowed={downPaymentValueIsAllowed}
        />

        <CustomSelect
          id="mortgageRateIndex"
          label="Räntebindningstid*"
          cirkatid
          value={appState.mortgageRateIndex}
          selectChangeHandler={mortgageRateIndexOnChangeHandler}
          options={appState.listInterests}
          interestDeviation={appState.interestDeviation}
          userIsChangingMortgageValues={appState.userIsChangingMortgageValues}
        />
      </div>
    </section>
  )
}

export default MortgageCalculator
